import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { UseDownloadRouteReportResult } from '../types/CleaningControls.types';
import { CleaningReportListActions } from 'app/modules/cleaning/cleaning-report-list/state/cleaningReportListActions';
import { selectExportRobotDetailsExportingId } from 'app/modules/cleaning/cleaning-report-list/state/cleaningReportListSelectors';
import { useAnalyticsLinkActivated } from 'app/cross-cutting-concerns/analytics/hooks/useAnalyticsLinkActivated';
import { AnalyticsLink } from 'app/cross-cutting-concerns/analytics/interfaces/Analytics.types';
import { Translations } from 'app/cross-cutting-concerns/translations/Translations';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';

/**
 * "Download report" of a timeline row: the report of that one route of the run, through the same export as the
 * route download of the routine drawer on the Reports page (not the export of the whole routine). The file is
 * downloaded when it is ready, by the app's async job handler.
 */
export const useDownloadRouteReport = (robot: Pick<Machine, 'id'>): UseDownloadRouteReportResult => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const analyticsLinkActivated = useAnalyticsLinkActivated();
  const exportingReportId = useSelector(selectExportRobotDetailsExportingId);

  const downloadReport = useCallback(
    (reportId: string): void => {
      analyticsLinkActivated({ linkName: AnalyticsLink.CLEANING_REPORTS_EXPORT_ROBOT_DETAILS });

      dispatch(
        CleaningReportListActions.requestExportRobotDetailsCleaningReportsRequest({
          id: reportId,
          timezone: DateTime.getBrowserTimeZone(),
          machineId: robot.id,
          lang: Translations.getSupportedLanguageCode(i18n.language),
        })
      );
    },
    [dispatch, analyticsLinkActivated, robot.id, i18n.language]
  );

  return { exportingReportId, downloadReport };
};
